import React, { useEffect, useMemo } from "react";
import { colors2, DropdownSingle, getVal, setVal, TextInput, UI_BODY_SEMIBOLD_SM } from "oolib";
import { useQueryClient } from "react-query";
import { extractAllBlocksFromTpl } from "@okf/ootils";

import { DataPivotResponsesList } from "@/components/analysisModules/DataPivot/comps/DataPivotResponsesList";
import { useGetDataLazy } from "@/utils/react-query-hooks/getData";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { useReportLandingContext } from "../../../ReportLandingContext";
import { useUpdateLoadedWidgetsTracker } from "../../../ReportLandingContext/useUpdateLoadedWidgetsTracker";
import { WidgetErrorState, WidgetLoadingState } from "../../WidgetRenderer/comps/altStatePlaceholders";
import { WidgetHeader } from "../WidgetBarChart/WIDGET_ELEM_ConfigForm";
import { WidgetFilters } from "../utils/WidgetFilters";
import { getDerivedBlockTitle } from "../WidgetBarChart/utils/_extractWidgetTitle";

/**
 * WidgetResponsesList - Displays all responses from a single text field
 *
 * Reuses the DataPivotResponsesList component for rendering responses
 * with infinite scroll, metadata display (contributor, date, link).
 */
export const Component = ({
  contentType,
  blockValuePath,
  activeFilters = [],
  widgetFilters = [],
  widgetIdx,
  id,
  title,
  style = {},
  // Public-share replay: a frozen { pages: [{ data:[...≤20], count }] } blob. When present
  // the widget renders this instead of fetching (and infinite scroll is disabled).
  prefetchedData,
}) => {
  const allTpls = useGetQueryData("allTpls");
  const { updateWidgetDataStore } = useReportLandingContext() || {};

  // Get block info from templates
  const activeBlock = useMemo(() => {
    if (!contentType || !blockValuePath || !allTpls) return null;
    const tpl = allTpls.find((t) => t.kp_content_type === contentType);
    if (!tpl) return null;
    const blocks = extractAllBlocksFromTpl({ tpl });
    return blocks.find((b) => b.valuePath === blockValuePath);
  }, [allTpls, contentType, blockValuePath]);

  // Combine report-level and widget-level filters
  const combinedFilters = useMemo(
    () => [...activeFilters, ...widgetFilters],
    [activeFilters, widgetFilters]
  );

  // Build find query - only fetch documents where the field exists
  const findQuery = useMemo(() => {
    const query = { kp_published_status: "published" };
    if (blockValuePath) {
      query[blockValuePath] = { $exists: true };
    }
    return query;
  }, [blockValuePath]);

  // Fetch data with infinite scroll (skipped entirely on public-share replay)
  const {
    data: dataFromApiCall,
    status: statusFromApiCall,
    error,
    hasNextPage: hasNextPageFromApiCall,
    isFetchingNextPage,
    fetchNextPage: fetchNextPageFromApiCall,
  } = useGetDataLazy({
    contentTypes: contentType ? [contentType] : [],
    findQuery,
    activeFilters: combinedFilters,
    projection: {
      [blockValuePath]: 1,
      "main.title": 1,
      "meta.kp_contributed_by": 1,
      kp_date_published: 1,
      "meta.kp_content_type": 1,
    },
    activeSort: { kp_date_published: -1 },
    population: [{ path: "meta.kp_contributed_by", model: "user" }],
    queryOptions: {
      enabled: !prefetchedData && !!activeBlock && !!contentType,
    },
  });

  // On replay, render the frozen snapshot; live infinite scroll is disabled.
  const data = prefetchedData || dataFromApiCall;
  const status = prefetchedData ? "success" : statusFromApiCall;
  const hasNextPage = prefetchedData ? false : hasNextPageFromApiCall;
  const fetchNextPage = prefetchedData ? () => {} : fetchNextPageFromApiCall;

  // Capture: push rendered responses to the central store so a share snapshot can replay
  // them. The public-snapshot size cap is applied by the snapshot builder, not here — this
  // widget stays agnostic to public-report concerns. No-op on replay.
  useEffect(() => {
    if (prefetchedData || status !== "success" || !data?.pages?.length) return;
    const flattened = data.pages.reduce((a, b) => [...a, ...b.data], []);
    updateWidgetDataStore &&
      updateWidgetDataStore(id, {
        pages: [{ data: flattened, count: data.pages[0]?.count }],
      });
  }, [prefetchedData, data, status, id, updateWidgetDataStore]);

  useUpdateLoadedWidgetsTracker({ status, widgetIdx });

  // Handle states
  if (!contentType || !blockValuePath) {
    return (
      <WidgetErrorState
        colorPreset="purple"
        error="Please configure content type and field"
      />
    );
  }

  if (!activeBlock) {
    return (
      <WidgetErrorState
        colorPreset="purple"
        error="Field not found in template"
      />
    );
  }

  if (status === "loading") {
    return <WidgetLoadingState />;
  }

  if (status === "error") {
    return <WidgetErrorState error={error} />;
  }

  // Get total count from first page
  const totalCount = data?.pages?.[0]?.count;

  // Reuse DataPivotResponsesList component
  return (
    <div style={{ maxHeight: "500px", overflowY: "auto", ...style }}>
      {/* Results count header */}
      <div
        style={{
          padding: "0.8rem 1.4rem",
          borderBottom: `1px solid ${colors2.grey10}`,
          position: "sticky",
          top: 0,
          backgroundColor: colors2.white,
          zIndex: 1,
        }}
      >
        <UI_BODY_SEMIBOLD_SM style={{ color: colors2.grey60 }}>
          {totalCount !== undefined ? `${totalCount} Responses` : "Loading..."}
        </UI_BODY_SEMIBOLD_SM>
      </div>

      <DataPivotResponsesList
        data={data}
        dataFetchStatus={status}
        dataFetchError={error}
        activeBlock={activeBlock}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
      />
    </div>
  );
};

// Widget metadata for registration
export const optionDef = {
  display: "Responses List",
  icon: "List",
  value: "responsesList",
  comp: "WidgetResponsesList",
  widgetLabel: "Responses List",
};

// Default configuration
export const defaultConfig = { props: {} };

// Validation - check required props are set
export const someReqPropsUnset = (config) => {
  return !config?.props?.contentType || !config?.props?.blockValuePath;
};

// Configuration form component
export const ConfigForm = ({
  thisWidgetConfig,
  setConfig,
  underEditWidgetIdx,
}) => {
  const dataKeys = {
    title: "props.title",
    blockValuePath: "props.blockValuePath",
    contentType: "props.contentType",
  };

  const queryClient = useQueryClient();
  const allTpls = queryClient.getQueryData("allTpls");

  // Filter to only publishing content types
  const contentTypeTpls = allTpls?.filter(
    (tpl) => tpl.general?.segment === "publishing"
  ) || [];

  // Build field options from all publishing templates.
  // Same block compatibility rule as DataPivot: include all blocks except StaticRichText
  // and inputOnly blocks.
  const blockOptions = useMemo(() => {
    return contentTypeTpls.reduce((acc, tpl) => {
      const blocks = extractAllBlocksFromTpl({ tpl });

      const pivotableBlocks = blocks
        .filter((block) => block.comp !== "StaticRichText" && block.inputOnly !== true)
        .map((block) => ({
          display:
            block.props?.label || block.props?.shortLabel || block.valuePath,
          desc: tpl.general?.content?.title || tpl.kp_content_type,
          value: `${tpl.kp_content_type}|${block.valuePath}`,
          contentType: tpl.kp_content_type,
          blockValuePath: block.valuePath,
        }));

      return [...acc, ...pivotableBlocks];
    }, []);
  }, [contentTypeTpls]);

  // Get current combined value for dropdown
  const currentValue = useMemo(() => {
    const contentType = getVal(thisWidgetConfig, dataKeys.contentType);
    const blockValuePath = getVal(thisWidgetConfig, dataKeys.blockValuePath);
    return contentType && blockValuePath
      ? `${contentType}|${blockValuePath}`
      : "";
  }, [thisWidgetConfig]);

  // Handle field selection
  const handleBlockSelection = (key, value) => {
    if (!value) return;

    const [contentType, blockValuePath] = value.split("|");

    setConfig((prev) => {
      let modifiedConfig = JSON.parse(JSON.stringify(prev[underEditWidgetIdx]));

      // Set contentType and blockValuePath
      modifiedConfig = setVal(modifiedConfig, dataKeys.contentType, contentType);
      modifiedConfig = setVal(
        modifiedConfig,
        dataKeys.blockValuePath,
        blockValuePath
      );

      // Auto-set title based on selection
      const derived = getDerivedBlockTitle({ contentType, blockValuePath });
      modifiedConfig = setVal(modifiedConfig, dataKeys.title, derived);

      const fullConfig = JSON.parse(JSON.stringify(prev));
      fullConfig[underEditWidgetIdx] = modifiedConfig;
      return fullConfig;
    });
  };

  // Handle title change
  const handleTitleChange = (key, value) => {
    setConfig((prev) => {
      let modifiedConfig = JSON.parse(JSON.stringify(prev[underEditWidgetIdx]));
      modifiedConfig = setVal(modifiedConfig, dataKeys.title, value);
      const fullConfig = JSON.parse(JSON.stringify(prev));
      fullConfig[underEditWidgetIdx] = modifiedConfig;
      return fullConfig;
    });
  };

  // Get content type for widget filters
  const contentTypeForFilters = useMemo(() => {
    const ct = getVal(thisWidgetConfig, dataKeys.contentType);
    return ct ? [ct] : [];
  }, [thisWidgetConfig]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.4rem" }}>
      <WidgetHeader icon="List" title="Responses List" />

      <TextInput
        id={dataKeys.title}
        label="Widget Title"
        placeholder="Enter title"
        value={getVal(thisWidgetConfig, dataKeys.title) || ""}
        onChange={handleTitleChange}
      />

      <DropdownSingle
        id="blockSelection"
        label="Select Text Field"
        placeholder="Choose a field to display responses"
        value={currentValue}
        onChange={handleBlockSelection}
        options={blockOptions}
        saveValueAsString
        popOutOfOverflowHiddenParent
      />

      <WidgetFilters
        widgetConfig={thisWidgetConfig}
        updateWidgetConfig={setConfig}
        widgetIndex={underEditWidgetIdx}
        contentTypesForFilters={contentTypeForFilters}
      />
    </div>
  );
};
